import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from 'sonner';
import { 
  Calendar, MapPin, Plane, Utensils, Sparkles, Heart, Users, 
  Star, Check, ChevronRight, Sun, Moon, Sunrise, ExternalLink,
  Wifi, Palmtree, Waves, Mountain, Leaf, Clock, Quote, Play, ArrowLeft,
  User, CreditCard, Crown, AlertCircle, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Accommodation data with actual images from Sister Diamonds website
const ACCOMMODATIONS = [
  {
    id: 'geodesic-dome',
    name: 'Sacred Geodesic Domes',
    icon: '🌿',
    description: 'Nestled in the lush rainforest along our private river, these domes invite you to reconnect with nature.',
    features: ['480 sq. ft. living space', 'Two King beds', 'Private jungle deck with hammock', 'Open-air rainforest shower', 'Full AC'],
    price: 5297,
    deposit: 1500,
    monthlyPayment: 949,
    paymentMonths: 4,
    occupancy: 'Double Occupancy',
    image: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/eif7vw9m_geodesic%20dome.jpeg',
    bookingUrl: 'https://events.sisterdiamonds.com/soft-power-domes'
  },
  {
    id: 'exotic-bungalow',
    name: 'Exotic Jungle Bungalows',
    icon: '🌺',
    description: 'Bali-inspired bungalows with spacious bathrooms opening to sweeping jungle views.',
    features: ['430 sq. ft. with jungle deck', 'One King or two Twin beds', 'Soaking tub with jungle views', 'AC available'],
    price: 4997,
    priceAlt: 5497,
    deposit: 1500,
    monthlyPayment: 874,
    monthlyPaymentAlt: 999,
    paymentMonths: 4,
    occupancy: 'Double or Single',
    image: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/72j2xc0z_exotic%20bungalow.png',
    bookingUrl: 'https://events.sisterdiamonds.com/soft-power-bungalows'
  },
  {
    id: 'bali-suite',
    name: 'Eloquent Bali Suites',
    icon: '✨',
    description: 'Perched atop the mountain in our 200-year-old Bali Village with breathtaking ocean views.',
    features: ['300 sq. ft. elegant space', 'One plush King bed', 'Panoramic ocean/garden views', 'Infinity pool access', 'Full AC'],
    price: 5997,
    deposit: 1500,
    monthlyPayment: 1124,
    paymentMonths: 4,
    occupancy: 'Single Occupancy',
    image: 'https://i.ontraport.com/260712.6f0bb3e980d13471c417a721d2630c62.PNG',
    bookingUrl: 'https://events.sisterdiamonds.com/soft-power-bali-suites',
    soldOut: true
  },
  {
    id: 'luxurious-villa',
    name: 'Luxurious Private Villas',
    icon: '🌸',
    description: 'Hand-carved Balinese treasures - 170-180 year old villas with private plunge pools.',
    features: ['Private villa sanctuary', 'King bed', 'Ocean & rainforest views', 'Private deck & plunge pool', 'Open-air design'],
    price: 6597,
    deposit: 1500,
    monthlyPayment: 1274,
    paymentMonths: 4,
    occupancy: 'Single Occupancy',
    image: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/shh7wly6_Luxury%20Villa.png',
    bookingUrl: 'https://events.sisterdiamonds.com/soft-power-luxurious-villas'
  }
];

// Host images - using uploaded photo
const HOST_IMAGES = {
  crystalAndNicole: 'https://customer-assets.emergentagent.com/job_volition-membership/artifacts/wdy6rj8w_crystal%20and%20drnicole.png',
  crystal: 'https://i.ontraport.com/260712.ab2146fd52a3bec566c017ab26b740c5.PNG',
  drNicole: 'https://i.ontraport.com/260712.96ce8faf24d6dd5d62bfdaa000961710.PNG'
};

// Additional retreat images
const RETREAT_IMAGES = {
  hero: 'https://i.ontraport.com/260712.dbe554751ad634586f9726bdc420b455.PNG',
  heroBg: 'https://i.ontraport.com/260712.f60c4925699779b8a8f9047f3666732c.PNG',
  costaRica1: 'https://i.ontraport.com/260712.155544a5758d1d35b09cacdf90ad353f.PNG',
  costaRica2: 'https://i.ontraport.com/260712.24d51fd5238aaff0d1e8dc4705bba054.PNG',
  costaRica3: 'https://i.ontraport.com/260712.e8246cf334e8430fccff91d4d75d391c.PNG',
  costaRica4: 'https://i.ontraport.com/260712.870393c33d2d384259e3a6a34fa7aa8f.PNG',
  dayInLife: 'https://i.ontraport.com/260712.628aa0ccfd9bfd523d39171beb40e866.PNG',
  culinary: 'https://i.ontraport.com/260712.528280e7e56642bcac794971c22d2efb.PNG',
  sisterhood: 'https://i.ontraport.com/260712.3ad5ee96d933e53b8081dc84e3d99808.PNG',
  gathering: 'https://i.ontraport.com/260712.726717efdfdf89ec33f8e110c365b47c.PNG',
  crystalRetreat: 'https://i.ontraport.com/260712.91e8ec861336e5d5010605cb337dbc35.PNG',
  drNicoleRetreat: 'https://i.ontraport.com/260712.9d27bdc206b7678cc5223c1d5d7de17e.PNG'
};

// Video testimonials
const VIDEO_TESTIMONIALS = [
  {
    name: "Tracy Higgs",
    title: "Director, Office of Investigations, Nuclear Regulatory Commission",
    vimeoId: "849606583",
    thumbnail: "https://i.vimeocdn.com/video/1488692012-638ac0591ff0d31c43673736ff881dbfaffb1d81d765a9895170bae0f4516f4e-d"
  },
  {
    name: "CeWyon Chandler-Ward",
    title: "Serial Entrepreneur | Angel Investor",
    vimeoId: "849760143",
    thumbnail: "https://i.vimeocdn.com/video/1489342970-59f6bd1640a0844f1cf844c0c52e8858da1c3718a81d1c7336311708bc3cabd5-d"
  },
  {
    name: "Arlene Henderson",
    title: "Business Consultant, Executive Coach",
    vimeoId: "849614055",
    thumbnail: "https://i.vimeocdn.com/video/1488709445-f620e9970488ed897bc868536f3e858af8a4498522a0d8a6d2717817580421ef-d"
  }
];

// Written testimonials
const WRITTEN_TESTIMONIALS = [
  {
    name: "Dr. Ursula Whitehead",
    title: "Leadership Coach",
    quote: "I am really grateful for the opportunity to work with Dr. Nicole and Crystal. They were just personal, inspiring and life-changing. If you're interested in making a change in your life and going to the next level, become a part of the Sister Diamond Collective. You will not regret it.",
    image: "https://i.ontraport.com/260712.2334b2d50dcbe67a0fd0db7cf0ad7e13.PNG"
  }
];

const FAQS = [
  {
    question: "Where do I fly into Costa Rica?",
    answer: "San Jose (Juan Santamaria) International Airport (SJO). Fly in by 7am on June 26, 2026 as we board a private jet around 9am. If arriving the day before, we can provide hotel options near SJO."
  },
  {
    question: "Do we take another airplane to Imiloa?",
    answer: "Yes! We'll take a private charter jet from SJO to Quepos (25 min flight) - included in your retreat. Ground transportation is also available at ~$300 each way (3.5 hours)."
  },
  {
    question: "What should I pack?",
    answer: "Sunglasses, beach hat, organic sunscreen & mosquito repellent, comfortable closed-toe shoes/boots (jungle trails can be muddy), sandals, swimwear, yoga attire, flashlight, reusable water bottle, and electrolytes."
  },
  {
    question: "Is there WiFi and what about power outlets?",
    answer: "Yes, WiFi is available on campus. WhatsApp is recommended for communication. Standard North American power outlets are used - bring an adapter if needed."
  },
  {
    question: "Is this retreat open to men?",
    answer: "No, this is an experience designed for and catered to the elevation and wellness of women."
  },
  {
    question: "Is there a lot of walking at Imiloa?",
    answer: "While not strenuous for most guests, you should be mobile and comfortable walking moderate distances, including stairs and hilly areas. Contact us if you have mobility concerns."
  },
  {
    question: "What is the cancellation policy?",
    answer: "All payments are non-refundable and non-transferable. Trip cancellation insurance is required to protect your investment."
  }
];

const RetreatLandingPage = () => {
  const { user, hasTier } = useAuth();
  const navigate = useNavigate();
  const [selectedAccommodation, setSelectedAccommodation] = useState(null);
  
  // Booking state
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [paymentOption, setPaymentOption] = useState('full'); // 'full' or 'plan'
  const [addVip, setAddVip] = useState(false);
  const [selectedVilla, setSelectedVilla] = useState('');

  // Room data with updated pricing
  const BOOKING_ROOMS = [
    {
      id: 'geodesic-dome',
      name: 'Sacred Geodesic Domes',
      icon: '🏕️',
      image: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/eif7vw9m_geodesic%20dome.jpeg',
      description: 'Nestled in the lush rainforest along our private river, reconnect with nature.',
      features: ['King Bed', 'Private jungle deck', 'Rainforest shower', 'Full AC'],
      pricePerPerson: 5297,
      occupancy: 'double',
      occupancyLabel: 'Double Occupancy Only',
      inventory: 12,
      bedType: '12 King Beds Available',
      deposit: 500,
      forms: {
        fullPay: 'p2c260712f44',
        plan3: 'p2c260712f47',
        plan2: 'p2c260712f48'
      }
    },
    {
      id: 'bungalow-double',
      name: 'Exotic Bungalow - Double',
      icon: '🌴',
      image: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/72j2xc0z_exotic%20bungalow.png',
      description: 'Bali-inspired bungalow with spacious bathroom and sweeping jungle views.',
      features: ['Twin Beds', 'Jungle deck', 'Soaking tub', 'AC available'],
      pricePerPerson: 4997,
      occupancy: 'double',
      occupancyLabel: 'Double Occupancy',
      inventory: 1,
      bedType: 'Last Double Available!',
      deposit: 500,
      forms: {
        fullPay: 'p2c260712f46',
        plan3: 'p2c260712f49',
        plan2: 'p2c260712f50'
      }
    },
    {
      id: 'bungalow-single',
      name: 'Exotic Bungalow - Single',
      icon: '🌴',
      image: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/72j2xc0z_exotic%20bungalow.png',
      description: 'Bali-inspired bungalow with spacious bathroom and sweeping jungle views. Private room for solo travelers.',
      features: ['Twin Bed', 'Jungle deck', 'Soaking tub', 'AC available'],
      pricePerPerson: 5997,
      occupancy: 'single',
      occupancyLabel: 'Single Occupancy',
      inventory: 3,
      bedType: '3 Single Rooms Available',
      deposit: 500,
      forms: {
        fullPay: 'p2c260712f45',
        plan3: 'p2c260712f51',
        plan2: 'p2c260712f52'
      }
    },
    {
      id: 'luxury-villa',
      name: 'Luxurious Private Villas',
      icon: '🏡',
      image: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/shh7wly6_Luxury%20Villa.png',
      description: 'Hand-carved 170-180 year old Balinese villas with private plunge pools.',
      features: ['King Bed', 'Private plunge pool', 'Ocean views', 'Open-air design'],
      pricePerPerson: 6597,
      occupancy: 'single',
      occupancyLabel: 'Single Occupancy',
      inventory: 2,
      bedType: '2 Villas: Jogolo & Potoo',
      deposit: 1000,
      villaOptions: ['The Jogolo House', 'The Potoo House'],
      forms: {
        fullPay: 'p2c260712f43',
        plan3: 'p2c260712f54',
        plan2: 'p2c260712f53'
      }
    },
    {
      id: 'bali-suite',
      name: 'Eloquent Bali Suites',
      icon: '✨',
      image: 'https://cdn.prod.website-files.com/68644f6dde20dea68414d670/68b52ec6f88632b6d6d2c7a7_Imiloa%20-%20Bali%201%20%26%202%20-%20ir11.avif',
      description: 'Perched atop the mountain in our 200-year-old Bali Village with breathtaking ocean views.',
      features: ['King Bed', 'Panoramic views', 'Infinity pool access', 'Full AC'],
      pricePerPerson: 5997,
      occupancy: 'single',
      occupancyLabel: 'Single Occupancy',
      inventory: 0,
      bedType: 'Sold Out',
      deposit: 500,
      forms: {
        fullPay: null,
        plan3: null,
        plan2: null
      }
    }
  ];

  const VIP_UPGRADE = {
    price: 1500,
    features: ['Priority access', 'VIP gatherings', 'Premium amenities', 'Concierge service']
  };

  // Determine available payment options based on current date
  const getPaymentOptions = () => {
    const now = new Date();
    const march1 = new Date(2026, 2, 1); // March 1, 2026
    const april1 = new Date(2026, 3, 1); // April 1, 2026
    const may1 = new Date(2026, 4, 1);   // May 1, 2026

    if (now < march1) {
      return { planAvailable: true, installments: 3, planKey: 'plan3', label: '3-Pay Plan', dueText: 'Due monthly through May' };
    } else if (now < april1) {
      return { planAvailable: true, installments: 2, planKey: 'plan2', label: '2-Pay Plan', dueText: 'Due monthly through May' };
    } else if (now < may1) {
      return { planAvailable: true, installments: 1, planKey: 'plan2', label: '2-Pay Plan', dueText: 'Final payment due before departure' };
    } else {
      return { planAvailable: false, installments: 0, planKey: null, label: null, dueText: null };
    }
  };

  const paymentOptions = getPaymentOptions();

  const getPaymentPlanDetails = (room) => {
    if (!room || !paymentOptions.planAvailable) return null;
    const remaining = room.pricePerPerson - room.deposit;
    const monthlyPayment = remaining / paymentOptions.installments;
    return { 
      deposit: room.deposit, 
      monthly: monthlyPayment.toFixed(2), 
      installments: paymentOptions.installments,
      dueText: paymentOptions.dueText
    };
  };

  const getTotalPrice = () => {
    if (!selectedRoom) return 0;
    let total = selectedRoom.pricePerPerson;
    if (addVip) total += VIP_UPGRADE.price;
    return total;
  };

  const handleCheckout = () => {
    if (!selectedRoom) return;
    
    let formKey;
    if (paymentOption === 'full') {
      formKey = 'fullPay';
    } else {
      formKey = paymentOptions.planKey; // 'plan3' or 'plan2' based on date
    }
    
    const formId = selectedRoom.forms[formKey];
    if (formId) {
      // Open Ontraport form in new window
      const ontraportUrl = `https://sisterdiamonds.ontraport.com/order-form/${formId}`;
      window.open(ontraportUrl, '_blank');
    } else {
      alert('This room option is currently sold out.');
    }
  };

  const paymentPlan = selectedRoom ? getPaymentPlanDetails(selectedRoom) : null;

  const handleBookNow = (accommodation) => {
    window.open(accommodation.bookingUrl, '_blank');
  };

  const handleConnectionCall = () => {
    window.open('https://calendly.com/crystalanddrnicole/justbesoftpowerretreatinterest', '_blank');
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${RETREAT_IMAGES.hero})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/20 to-transparent" />
        </div>
        
        {/* Back to Home Link */}
        <div className="absolute top-6 left-6 z-20">
          <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white transition-colors bg-black/20 px-4 py-2 rounded-full backdrop-blur-sm">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-32">
          <p className="inline-block bg-forest/80 text-white font-semibold tracking-widest uppercase mb-4 text-sm px-4 py-2 rounded-full">
            Imiloa Institute, Costa Rica • June 26 – July 1, 2026
          </p>
          <h1 className="font-heading text-5xl md:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
            Just Be: <br />
            <span className="text-terracotta drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">Soft Power</span>™ Retreat
          </h1>
          <p className="text-xl md:text-2xl text-white mb-8 max-w-2xl mx-auto leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]">
            A soul-deep, luxury retreat for high-capacity women ready to release, restore, and rise in their softness, not their striving.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => document.getElementById('accommodations').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-terracotta hover:bg-terracotta/90 text-white px-8 py-6 text-lg shadow-xl"
              data-testid="hero-book-retreat-btn"
            >
              Book Your Retreat
            </Button>
            <Button 
              onClick={handleConnectionCall}
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg shadow-xl"
              data-testid="hero-connection-call-btn"
            >
              Book a Connection Call
            </Button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className="h-8 w-8 text-white rotate-90" />
        </div>
      </section>

      {/* Navigation Bar (sticky) */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-sand shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="font-heading text-xl font-bold text-forest">
              Sister Diamonds
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <a href="#about" className="text-gray-600 hover:text-forest transition">About</a>
              <a href="#experience" className="text-gray-600 hover:text-forest transition">Experience</a>
              <a href="#accommodations" className="text-gray-600 hover:text-forest transition">Stay</a>
              <a href="#hosts" className="text-gray-600 hover:text-forest transition">Hosts</a>
              <a href="#faq" className="text-gray-600 hover:text-forest transition">FAQ</a>
            </div>
            {user ? (
              <Link to="/dashboard">
                <Button variant="outline" size="sm" className="btn-outline">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button size="sm" className="bg-forest text-white hover:bg-forest/90">
                  Sign In
                </Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* About Section */}
      <section id="about" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-terracotta/10 text-terracotta mb-4">For High-Capacity Women</Badge>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mb-6">
                You Don&apos;t Need Another Checklist Disguised as Self-Care
              </h2>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>
                  You&apos;ve done enough. You&apos;ve built the life, the brand, the legacy—but deep down, you&apos;re quietly asking: <em>&quot;Where do I go to be poured into without performing?&quot;</em>
                </p>
                <p>
                  This retreat is for the woman who feels the shift coming but needs space to hear what God is saying. It&apos;s for the woman who&apos;s tired of being strong every damn day, and is ready to remember what softness feels like.
                </p>
                <p className="font-semibold text-forest">
                  This is not a reward for doing more.<br />
                  This is your permission to Just Be.
                </p>
              </div>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-sand/30 rounded-xl">
                  <Heart className="h-6 w-6 text-terracotta" />
                  <span className="font-medium text-forest">Soul-Deep Rest</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-sand/30 rounded-xl">
                  <Users className="h-6 w-6 text-terracotta" />
                  <span className="font-medium text-forest">Sacred Sisterhood</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-sand/30 rounded-xl">
                  <Sparkles className="h-6 w-6 text-terracotta" />
                  <span className="font-medium text-forest">Transformation</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-sand/30 rounded-xl">
                  <Palmtree className="h-6 w-6 text-terracotta" />
                  <span className="font-medium text-forest">Luxury Setting</span>
                </div>
              </div>
              
              <div className="mt-8">
                <Button 
                  onClick={() => document.getElementById('accommodations').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-terracotta hover:bg-terracotta/90 text-white"
                >
                  Reserve Your Spot <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <img 
                src={RETREAT_IMAGES.costaRica1}
                alt="Costa Rica retreat" 
                className="rounded-2xl h-64 w-full object-cover"
              />
              <img 
                src={RETREAT_IMAGES.costaRica2}
                alt="Costa Rica nature" 
                className="rounded-2xl h-64 w-full object-cover mt-8"
              />
              <img 
                src={RETREAT_IMAGES.costaRica3}
                alt="Luxury accommodation" 
                className="rounded-2xl h-64 w-full object-cover -mt-8"
              />
              <img 
                src={RETREAT_IMAGES.costaRica4}
                alt="Wellness moment" 
                className="rounded-2xl h-64 w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Costa Rica */}
      <section className="py-20 px-6 bg-forest text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
            Why Costa Rica?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            This isn&apos;t just about the destination. It&apos;s about the energy you need to return to yourself. Costa Rica was chosen with intention because the land holds a sacred rhythm that softens even the strongest woman.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-white/10 rounded-2xl">
              <Mountain className="h-10 w-10 mx-auto mb-4 text-terracotta" />
              <h3 className="font-heading text-xl font-semibold mb-2">The Mountains</h3>
              <p className="text-white/80">Don&apos;t judge you. They hold space for your truth.</p>
            </div>
            <div className="p-6 bg-white/10 rounded-2xl">
              <Leaf className="h-10 w-10 mx-auto mb-4 text-terracotta" />
              <h3 className="font-heading text-xl font-semibold mb-2">The Jungle</h3>
              <p className="text-white/80">Doesn&apos;t rush you. It invites you to breathe.</p>
            </div>
            <div className="p-6 bg-white/10 rounded-2xl">
              <Waves className="h-10 w-10 mx-auto mb-4 text-terracotta" />
              <h3 className="font-heading text-xl font-semibold mb-2">The Breeze</h3>
              <p className="text-white/80">Doesn&apos;t ask you to lead, fix, or perform.</p>
            </div>
          </div>
          <div className="mt-10">
            <Button 
              onClick={() => document.getElementById('accommodations').scrollIntoView({ behavior: 'smooth' })}
              className="bg-terracotta hover:bg-terracotta/90 text-white"
            >
              Reserve Your Spot <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* A Day in the Life */}
      <section id="experience" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-terracotta/10 text-terracotta mb-4">The Experience</Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mb-4">
              A Day in the Life at Just Be
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Every moment is curated to restore your rhythm, not control it. No rush. No pressure to perform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="card-base border-t-4 border-t-yellow-400">
              <CardContent className="p-6">
                <Sunrise className="h-10 w-10 text-yellow-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-forest mb-2">Morning</h3>
                <p className="text-sm text-gray-500 mb-3">Stillness + Soul</p>
                <p className="text-gray-600">
                  Wake to birdsong. Nourishing breakfast. Soul-stirring sessions with Crystal & Dr. Nicole. Begin grounded and centered.
                </p>
              </CardContent>
            </Card>

            <Card className="card-base border-t-4 border-t-blue-400">
              <CardContent className="p-6">
                <Sun className="h-10 w-10 text-blue-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-forest mb-2">Midday</h3>
                <p className="text-sm text-gray-500 mb-3">Rest + Reconnection</p>
                <p className="text-gray-600">
                  Nap. Walk the jungle path. Swim beneath the waterfall. Long, laughter-filled lunch. Rest isn&apos;t earned—it&apos;s expected.
                </p>
              </CardContent>
            </Card>

            <Card className="card-base border-t-4 border-t-orange-400">
              <CardContent className="p-6">
                <Sparkles className="h-10 w-10 text-orange-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-forest mb-2">Afternoon</h3>
                <p className="text-sm text-gray-500 mb-3">Unmasking + Breakthroughs</p>
                <p className="text-gray-600">
                  Enter sacred space. Speak truths you&apos;ve never had space to say. Discover strength in softness.
                </p>
              </CardContent>
            </Card>

            <Card className="card-base border-t-4 border-t-purple-400">
              <CardContent className="p-6">
                <Moon className="h-10 w-10 text-purple-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-forest mb-2">Evening</h3>
                <p className="text-sm text-gray-500 mb-3">Ritual + Restoration</p>
                <p className="text-gray-600">
                  Candlelit dinners. Laughter. Tears. Easier breathing. Feel more you than you have in years.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-12">
            <Button 
              onClick={() => document.getElementById('accommodations').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-terracotta hover:bg-terracotta/90 text-white"
            >
              Reserve Your Spot <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-6 bg-sand/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <Badge className="bg-forest text-white mb-4">Everything You Need</Badge>
              <h2 className="font-heading text-4xl font-bold text-forest mb-6">
                What&apos;s Included
              </h2>
              <div className="space-y-4">
                {[
                  'Private charter jet from San José to Quepos & return',
                  'Round-trip ground transportation to Imiloa',
                  '5 nights / 6 days all-inclusive immersive experience',
                  'Daily transformational sessions with Crystal & Dr. Nicole',
                  'Eco-luxury Bali-style accommodations',
                  'Sacred sisterhood circles & rituals',
                  'Private waterfall, infinity pool, sauna access',
                  'All gourmet plant-based, organic meals',
                  'Daily housekeeping & full-service staff',
                  'Completely private campus for our group'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-terracotta mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Badge className="bg-gray-200 text-gray-700 mb-4">Plan Ahead</Badge>
              <h2 className="font-heading text-4xl font-bold text-forest mb-6">
                What&apos;s Not Included
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>• International airfare to/from San José (SJO)</p>
                <p>• Travel insurance (Required)</p>
                <p>• Alcoholic beverages (Imiloa is alcohol-free)</p>
                <p>• Souvenirs & personal shopping</p>
                <p>• Laundry services (available at extra cost)</p>
                <p>• Spa treatments & specialty drinks beyond provided</p>
                <p>• Wellness bar access</p>
              </div>
              
              <Card className="mt-8 bg-terracotta/10 border-terracotta/30">
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-bold text-forest mb-2">
                    🌸 VIP Add-On Available
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Private dinner with hosts, 60-min coaching session, wellness bar access, $150 spa credit, and meditation toolkit.
                  </p>
                  <p className="font-bold text-terracotta">Value: $3,500</p>
                </CardContent>
              </Card>
              
              <div className="mt-8">
                <Button 
                  onClick={() => document.getElementById('accommodations').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-forest hover:bg-forest/90 text-white"
                >
                  Reserve Your Spot <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accommodations */}
      {/* Book Your Retreat Experience */}
      <section id="accommodations" className="py-20 px-6 bg-gradient-to-b from-sand/30 to-white">
        <div className="max-w-6xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <Badge className="bg-terracotta/10 text-terracotta mb-4">Reserve Your Spot</Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mb-4">
              Book Your Retreat Experience
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Select your accommodation and payment option below
            </p>
          </div>

          {/* Step 1: Select Room */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
              <h3 className="font-heading text-xl md:text-2xl text-forest">Select Your Accommodation</h3>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {BOOKING_ROOMS.map((room) => (
                <Card 
                  key={room.id}
                  className={`cursor-pointer transition-all duration-300 overflow-hidden ${
                    selectedRoom?.id === room.id 
                      ? 'ring-2 ring-forest shadow-xl scale-[1.02]' 
                      : 'hover:shadow-lg hover:scale-[1.01]'
                  } ${room.inventory === 0 ? 'opacity-60' : ''}`}
                  onClick={() => room.inventory > 0 && setSelectedRoom(room)}
                  data-testid={`room-${room.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={room.image} alt={room.name} className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3">
                      {room.inventory > 0 ? (
                        <Badge className={`${room.inventory <= 3 ? 'bg-red-500' : 'bg-forest'} text-white`}>
                          {room.inventory} {room.inventory === 1 ? 'spot' : 'spots'} left
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-500 text-white">Sold Out</Badge>
                      )}
                    </div>
                    {selectedRoom?.id === room.id && (
                      <div className="absolute top-3 left-3 h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center">
                        <Check className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-5">
                    <h4 className="font-heading text-lg text-forest mb-1">{room.icon} {room.name}</h4>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{room.description}</p>
                    <div className="flex items-center gap-2 mb-3">
                      {room.occupancy === 'double' ? <Users className="h-4 w-4 text-forest" /> : <User className="h-4 w-4 text-forest" />}
                      <span className="text-sm text-gray-600">{room.occupancyLabel}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">{room.bedType}</p>
                    <div className="border-t pt-3">
                      <span className="text-2xl font-bold text-forest">${room.pricePerPerson.toLocaleString()}</span>
                      <span className="text-gray-500 text-sm"> per person</span>
                      {room.note && <p className="text-xs text-terracotta mt-1">{room.note}</p>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Step 2: Payment Option */}
          {selectedRoom && (
            <div className="mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <h3 className="font-heading text-xl md:text-2xl text-forest">Choose Payment Option</h3>
              </div>
              <div className={`grid gap-4 ${paymentOptions.planAvailable ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md'}`}>
                <Card 
                  className={`cursor-pointer transition-all ${paymentOption === 'full' ? 'ring-2 ring-forest shadow-lg' : 'hover:shadow-md'}`}
                  onClick={() => setPaymentOption('full')}
                  data-testid="payment-full"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-6 w-6 text-forest" />
                        <div>
                          <h4 className="font-heading text-lg text-forest">Pay in Full</h4>
                          <p className="text-sm text-gray-500">One-time payment</p>
                        </div>
                      </div>
                      <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${paymentOption === 'full' ? 'border-forest bg-forest' : 'border-gray-300'}`}>
                        {paymentOption === 'full' && <Check className="h-4 w-4 text-white" />}
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-forest">${selectedRoom.pricePerPerson.toLocaleString()}</div>
                  </CardContent>
                </Card>
                
                {paymentOptions.planAvailable ? (
                  <Card 
                    className={`cursor-pointer transition-all ${paymentOption === 'plan' ? 'ring-2 ring-forest shadow-lg' : 'hover:shadow-md'}`}
                    onClick={() => setPaymentOption('plan')}
                    data-testid="payment-plan"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Calendar className="h-6 w-6 text-forest" />
                          <div>
                            <h4 className="font-heading text-lg text-forest">{paymentOptions.label}</h4>
                            <p className="text-sm text-gray-500">Split into {paymentOptions.installments + 1} payments</p>
                          </div>
                        </div>
                        <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${paymentOption === 'plan' ? 'border-forest bg-forest' : 'border-gray-300'}`}>
                          {paymentOption === 'plan' && <Check className="h-4 w-4 text-white" />}
                        </div>
                      </div>
                      {paymentPlan && (
                        <div>
                          <div className="text-2xl font-bold text-forest mb-1">
                            ${paymentPlan.deposit.toLocaleString()} <span className="text-base font-normal text-gray-500">down today</span>
                          </div>
                          <p className="text-sm text-gray-600">+ {paymentPlan.installments} payments of ${paymentPlan.monthly}</p>
                          <p className="text-xs text-gray-400 mt-1">{paymentPlan.dueText}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Payment plans are no longer available</p>
                      <p className="text-xs text-amber-600 mt-1">Full payment is required for bookings after May 1st</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: VIP Upgrade */}
          {selectedRoom && (
            <div className="mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '150ms' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                <h3 className="font-heading text-xl md:text-2xl text-forest">VIP Upgrade (Optional)</h3>
              </div>
              <Card 
                className={`cursor-pointer transition-all ${addVip ? 'ring-2 ring-terracotta shadow-lg bg-terracotta/5' : 'hover:shadow-md'}`}
                onClick={() => setAddVip(!addVip)}
                data-testid="vip-upgrade"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center ${addVip ? 'bg-terracotta text-white' : 'bg-terracotta/10 text-terracotta'}`}>
                        <Crown className="h-6 w-6" />
                      </div>
                      <div>
                        <h4 className="font-heading text-xl text-forest mb-1">VIP Experience Upgrade</h4>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {VIP_UPGRADE.features.map((feature, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs bg-white">{feature}</Badge>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Full payment only - no payment plan for VIP
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-2xl font-bold text-terracotta">+${VIP_UPGRADE.price.toLocaleString()}</div>
                      <div className={`h-6 w-6 rounded border-2 flex items-center justify-center ${addVip ? 'border-terracotta bg-terracotta' : 'border-gray-300'}`}>
                        {addVip && <Check className="h-4 w-4 text-white" />}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Villa Selection */}
          {selectedRoom?.id === 'luxury-villa' && (
            <div className="mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">
                  <Sparkles className="h-4 w-4" />
                </div>
                <h3 className="font-heading text-xl md:text-2xl text-forest">Select Your Villa</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {selectedRoom.villaOptions.map((villa) => (
                  <Card 
                    key={villa}
                    className={`cursor-pointer transition-all ${selectedVilla === villa ? 'ring-2 ring-forest shadow-lg' : 'hover:shadow-md'}`}
                    onClick={() => setSelectedVilla(villa)}
                  >
                    <CardContent className="p-5 flex items-center justify-between">
                      <div>
                        <h4 className="font-heading text-lg text-forest">🌿 {villa}</h4>
                        <p className="text-sm text-gray-500">1 King Bed • Private Plunge Pool</p>
                      </div>
                      <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${selectedVilla === villa ? 'border-forest bg-forest' : 'border-gray-300'}`}>
                        {selectedVilla === villa && <Check className="h-4 w-4 text-white" />}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Order Summary & Checkout */}
          {selectedRoom && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '300ms' }}>
              <Card className="bg-forest text-white overflow-hidden">
                <CardContent className="p-6 md:p-8">
                  <h3 className="font-heading text-xl mb-6">Order Summary</h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-white/80">{selectedRoom.name}</span>
                      <span>${selectedRoom.pricePerPerson.toLocaleString()}</span>
                    </div>
                    {addVip && (
                      <div className="flex justify-between">
                        <span className="text-white/80">VIP Upgrade</span>
                        <span>+${VIP_UPGRADE.price.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="border-t border-white/20 pt-3 flex justify-between text-xl font-bold">
                      <span>Total</span>
                      <span>${getTotalPrice().toLocaleString()}</span>
                    </div>
                    {paymentOption === 'plan' && !addVip && paymentPlan && (
                      <div className="bg-white/10 rounded-lg p-4 mt-4">
                        <p className="text-sm text-white/80 mb-2">Payment Schedule:</p>
                        <p className="font-medium">Today: ${paymentPlan.deposit.toLocaleString()} deposit</p>
                        <p className="text-sm text-white/70">Then {paymentPlan.installments} payments of ${paymentPlan.monthly}</p>
                      </div>
                    )}
                    {addVip && paymentOption === 'plan' && (
                      <div className="bg-terracotta/20 rounded-lg p-4 mt-4 flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">VIP upgrade requires full payment. You&apos;ll be charged the full amount today.</p>
                      </div>
                    )}
                  </div>
                  <Button 
                    size="lg" 
                    className="w-full bg-terracotta hover:bg-terracotta/90 text-white h-14 text-lg"
                    onClick={handleCheckout}
                    data-testid="checkout-btn"
                  >
                    Proceed to Checkout
                  </Button>
                  <p className="text-center text-white/60 text-sm mt-4">
                    You&apos;ll be redirected to our secure payment portal
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* No Selection */}
          {!selectedRoom && (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Select an accommodation above to continue</p>
            </div>
          )}

          {/* Connection Call CTA */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Not sure which room is right for you?</p>
            <Button 
              onClick={handleConnectionCall}
              variant="outline"
              size="lg"
              className="btn-outline"
            >
              Schedule a Connection Call
            </Button>
          </div>
        </div>
      </section>

      {/* Meet Your Hosts */}
      <section id="hosts" className="py-20 px-6 bg-forest text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-white/20 text-white mb-4">Your Guides</Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Meet Your Hosts
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Combined Photo */}
            <div className="flex justify-center">
              <div className="rounded-2xl overflow-hidden border-4 border-terracotta shadow-2xl max-w-md">
                <img 
                  src={HOST_IMAGES.crystalAndNicole}
                  alt="Crystal Khalil & Dr. Nicole LaBeach"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            {/* Bios */}
            <div className="space-y-8">
              <div>
                <h3 className="font-heading text-2xl font-bold mb-2">Crystal Khalil</h3>
                <p className="text-terracotta font-medium mb-3">The Divine Disruptor &amp; Destiny Dealer</p>
                <p className="text-white/80 leading-relaxed">
                  The strategist with soul, the executive whisperer. From boardrooms to Bible study, she moves with purpose and power. With 30+ years of corporate savvy and a heart for service, she&apos;s built a movement through Sister Diamonds and Volition Enterprises.
                </p>
              </div>

              <div className="border-t border-white/20 pt-8">
                <h3 className="font-heading text-2xl font-bold mb-2">Dr. Nicole LaBeach</h3>
                <p className="text-terracotta font-medium mb-3">The Soul Surgeon &amp; Sacred Strategist</p>
                <p className="text-white/80 leading-relaxed">
                  With a Ph.D. in psychology and 25+ years of experience, Dr. Nicole transforms lives. Featured on GMA, CBS Radio, Oprah Daily, Essence, and as the 3-season host of &quot;Put A Ring On It&quot; on OWN. She holds space for you to remember who you were before the world told you who to be.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section className="py-20 px-6 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-terracotta/10 text-terracotta mb-4">Testimonials</Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mb-4">
              Hear From Our Sisters
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Real transformation stories from women who&apos;ve experienced our coaching and retreats.
            </p>
          </div>

          {/* Video Testimonials Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {VIDEO_TESTIMONIALS.map((testimonial, index) => (
              <Card key={index} className="card-base overflow-hidden group" data-testid={`video-testimonial-${index}`}>
                <a 
                  href={`https://vimeo.com/${testimonial.vimeoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="relative aspect-video bg-gray-900">
                    <img 
                      src={testimonial.thumbnail}
                      alt={`Testimonial from ${testimonial.name}`}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-16 w-16 rounded-full bg-terracotta/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="h-8 w-8 text-white ml-1" />
                      </div>
                    </div>
                  </div>
                </a>
                <CardContent className="p-4">
                  <p className="font-heading font-semibold text-forest">{testimonial.name}</p>
                  <p className="text-sm text-gray-500 line-clamp-2">{testimonial.title}</p>
                  <a 
                    href={`https://vimeo.com/${testimonial.vimeoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-terracotta text-sm font-medium mt-2 hover:underline"
                  >
                    <Play className="h-4 w-4" /> Watch on Vimeo
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Written Testimonial */}
          {WRITTEN_TESTIMONIALS.map((testimonial, index) => (
            <Card key={index} className="card-base max-w-3xl mx-auto overflow-hidden">
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row items-center">
                  <div className="w-full md:w-1/3 h-48 md:h-auto flex-shrink-0">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8 flex-1">
                    <Quote className="h-10 w-10 text-terracotta/30 mb-4" />
                    <p className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                      &quot;{testimonial.quote}&quot;
                    </p>
                    <div>
                      <p className="font-heading text-xl font-semibold text-forest">{testimonial.name}</p>
                      <p className="text-terracotta">{testimonial.title}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Payment Options */}
      <section className="py-20 px-6 bg-terracotta/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl font-bold text-forest mb-4">
            Flexible Payment Options
          </h2>
          <p className="text-gray-600 mb-8">Choose the path that works best for you</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="card-base border-2 border-forest/20">
              <CardContent className="p-8">
                <Badge className="bg-forest/10 text-forest mb-4">Pay In Full</Badge>
                <p className="text-gray-600 mb-2">Starting from</p>
                <p className="text-5xl font-bold text-forest mb-2">$4,997</p>
                <p className="text-gray-500">Single Payment</p>
                <p className="text-sm text-terracotta mt-2">Best value - save on monthly fees</p>
              </CardContent>
            </Card>
            <Card className="card-base border-2 border-terracotta/40 bg-terracotta/5">
              <CardContent className="p-8">
                <Badge className="bg-terracotta text-white mb-4">Most Popular</Badge>
                <p className="text-gray-600 mb-2">4-Month Payment Plan</p>
                <div className="mb-2">
                  <span className="text-3xl font-bold text-forest">$1,500</span>
                  <span className="text-gray-600 ml-2">deposit</span>
                </div>
                <p className="text-gray-600 mb-2">Then 4 monthly payments of</p>
                <p className="text-4xl font-bold text-terracotta mb-2">$874 - $1,274</p>
                <p className="text-sm text-gray-500">Based on accommodation choice</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="mt-8 p-4 bg-white rounded-lg border border-sand">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-forest">Payment Protection:</span> All payments are non-refundable and non-transferable. 
              Trip cancellation insurance is required to protect your investment.
            </p>
          </div>
          
          <Button 
            onClick={handleConnectionCall}
            size="lg"
            className="mt-8 bg-terracotta hover:bg-terracotta/90 text-white px-12 py-6 text-lg"
          >
            Let&apos;s Talk, Sis — Schedule a Call
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-terracotta/10 text-terracotta mb-4">Questions?</Badge>
            <h2 className="font-heading text-4xl font-bold text-forest">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl border border-sand px-6">
                <AccordionTrigger className="font-heading text-lg text-forest hover:no-underline py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-forest text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
            Rest is Not the Retreat.<br />
            <span className="text-terracotta">Rest is the Return.</span>
          </h2>
          <p className="text-xl text-white/90 mb-8">
            You will not leave the way you came. And you won&apos;t walk this path alone.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => document.getElementById('accommodations').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-terracotta hover:bg-terracotta/90 text-white px-8 py-6 text-lg"
            >
              Book Your Retreat
            </Button>
            <Button 
              onClick={handleConnectionCall}
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
            >
              Book a Connection Call
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-forest/95 text-white/60 text-center text-sm">
        <p>©2025 Sister Diamonds, LLC. All Rights Reserved.</p>
        <p className="mt-2">
          Questions? Contact us at <a href="mailto:info@sisterdiamonds.com" className="text-terracotta hover:underline">info@sisterdiamonds.com</a>
        </p>
      </footer>
    </div>
  );
};

export default RetreatLandingPage;
